import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useTheme } from '../contexts/ThemeContext';
import { useBroker } from '../contexts/BrokerContext';
import { Sun, Moon, Building2 } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { login, isAuthenticated } = useAuth();
  const { t } = useLocale();
  const { theme, toggleTheme } = useTheme();
  const { brokerInfo, brokerLoading } = useBroker();

  if (isAuthenticated) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (!result.success) {
      setError(result.error);
      setLoading(false);
    }
  };

  const logoUrl = brokerInfo?.logo_url
    ? brokerInfo.logo_url.startsWith('http') ? brokerInfo.logo_url : brokerInfo.logo_url
    : null;

  const brokerName = brokerInfo?.name || 'Broker Admin';
  const primaryColor = brokerInfo?.primary_color || 'var(--accent-primary)';
  const secondaryColor = brokerInfo?.secondary_color || 'var(--accent-info)';

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4">

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-4 w-96 h-96 rounded-full blur-3xl opacity-20"
          style={{ backgroundColor: primaryColor }} />
        <div className="absolute bottom-0 -right-4 w-96 h-96 rounded-full blur-3xl opacity-15"
          style={{ backgroundColor: secondaryColor }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-[120px] opacity-5"
          style={{ backgroundColor: primaryColor }} />
      </div>

      <button onClick={toggleTheme}
        className="absolute top-6 right-6 p-3 rounded-lg bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)] transition-smooth">
        {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>

      <div className="w-full max-w-md relative z-10">
        <div className="bg-[var(--bg-secondary)] rounded-2xl border border-[var(--border-color)] p-8 glass">

          {/* Broker Identity */}
          <div className="text-center mb-8">
            {brokerLoading ? (
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-2xl animate-pulse" style={{ backgroundColor: 'var(--bg-tertiary)' }} />
                <div className="h-5 w-40 rounded-lg animate-pulse" style={{ backgroundColor: 'var(--bg-tertiary)' }} />
                <div className="h-3 w-28 rounded-lg animate-pulse" style={{ backgroundColor: 'var(--bg-tertiary)' }} />
              </div>
            ) : logoUrl ? (
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center overflow-hidden shadow-lg p-1"
                  style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
                  <img src={logoUrl} alt={brokerName} className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }} />
                  <span className="hidden w-full h-full items-center justify-center text-xl font-black text-white rounded-xl"
                    style={{ backgroundColor: primaryColor, display: 'none' }}>
                    {brokerName.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h1 className="text-2xl font-black" style={{ color: 'var(--text-primary)' }}>{brokerName}</h1>
                  <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>{t('auth.adminPanel')}</p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg"
                  style={{ backgroundColor: primaryColor }}>
                  <Building2 className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-black" style={{ color: 'var(--text-primary)' }}>{brokerName}</h1>
                  <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>{t('auth.adminPanel')}</p>
                </div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border-color)' }} />
            <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--text-tertiary)' }}>
              {t('auth.login')}
            </span>
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border-color)' }} />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 rounded-lg bg-[var(--accent-danger)]/10 border border-[var(--accent-danger)]/20 text-[var(--accent-danger)] text-sm">
                {error}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-2">{t('auth.email')}</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-color)] focus:outline-none transition-smooth"
                style={{ '--tw-ring-color': primaryColor }}
                onFocus={e => e.target.style.borderColor = primaryColor}
                onBlur={e => e.target.style.borderColor = ''}
                placeholder="admin@broker.com" required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">{t('auth.password')}</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-color)] focus:outline-none transition-smooth"
                onFocus={e => e.target.style.borderColor = primaryColor}
                onBlur={e => e.target.style.borderColor = ''}
                placeholder="••••••••" required />
            </div>
            <button type="submit" disabled={loading}
              className="w-full py-3 rounded-lg text-white font-semibold transition-smooth disabled:opacity-50 disabled:cursor-not-allowed mt-2"
              style={{ backgroundColor: primaryColor }}
              onMouseEnter={e => { if (!loading) e.target.style.filter = 'brightness(1.1)'; }}
              onMouseLeave={e => { e.target.style.filter = ''; }}>
              {loading ? t('common.loading') : t('auth.signIn')}
            </button>
          </form>

          {/* Footer */}
          {brokerInfo?.support_email && (
            <p className="text-center text-xs mt-6" style={{ color: 'var(--text-tertiary)' }}>
              {t('auth.needHelp')}{' '}
              <a href={`mailto:${brokerInfo.support_email}`} className="font-semibold hover:underline"
                style={{ color: primaryColor }}>
                {brokerInfo.support_email}
              </a>
            </p>
          )}
        </div>

        <p className="text-center text-xs mt-4" style={{ color: 'var(--text-tertiary)' }}>
          {t('auth.poweredBy')}{' '}
          <span className="font-semibold" style={{ color: primaryColor }}>AMORTRADER</span>
        </p>
      </div>
    </div>
  );
}